import { Fragment } from 'react'
import { Link } from 'react-router'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { customerTabHref } from '@/lib/customerTabs'
import { formatCurrency, WHOLE_DOLLARS } from '@/lib/formatters'
import type { ClosedWonAccount } from '@/models/salesAnalytics'
import { LINE_COLORS } from './PremiumByLineCard'
import { PremiumFigure } from './PremiumFigure'

export function PremiumBreakdownCard({ accounts }: { accounts: ClosedWonAccount[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Premium line by line</CardTitle>
        <p className="text-sm text-neutral-500">
          Every closed-won customer broken into what it actually bought, carrier by carrier — one customer can
          take cargo from one carrier and liability from another. Taxes and fees are shown as their own line
          rather than spread across coverages, because they belong to the policy and not to any one of them.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Carrier</TableHead>
                <TableHead>Line</TableHead>
                <TableHead>Bound</TableHead>
                <TableHead>Policy start</TableHead>
                <TableHead className="text-right">Premium</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-sm text-neutral-500">
                    Nothing in range.
                  </TableCell>
                </TableRow>
              )}
              {accounts.map((account) => (
                <Fragment key={account.orgId}>
                  <TableRow className="bg-neutral-50 dark:bg-neutral-900">
                    <TableCell className="font-medium">
                      <Link to={customerTabHref(account.orgId)} className="underline underline-offset-2">
                        {account.account}
                      </Link>
                    </TableCell>
                    <TableCell>{account.carriers.join(', ') || '—'}</TableCell>
                    <TableCell className="text-neutral-500">
                      {account.coverageCount} {account.coverageCount === 1 ? 'coverage' : 'coverages'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{account.firstBoundDate}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {account.firstPolicyStart ?? (
                        <span className="text-amber-700 dark:text-amber-400">no start date</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium whitespace-nowrap tabular-nums">
                      <PremiumFigure
                        premium={account.premium}
                        lines={account.lineCount}
                        unpricedLines={account.unpricedLines}
                      />
                    </TableCell>
                  </TableRow>
                  {account.policies.map((policy) =>
                    policy.lines.length === 0 ? (
                      // A policy with nothing on it still says so, instead of vanishing under a priced sibling.
                      <TableRow key={policy.policyId}>
                        <TableCell />
                        <TableCell className="text-neutral-500">{policy.vendor || '—'}</TableCell>
                        <TableCell className="text-neutral-500">no coverages</TableCell>
                        <TableCell className="whitespace-nowrap text-neutral-500 tabular-nums">
                          {policy.boundDate}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-neutral-500 tabular-nums">
                          {policy.policyStart ?? (
                            <span className="text-amber-700 dark:text-amber-400">no start date</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-amber-700 dark:text-amber-400">no premium</span>
                        </TableCell>
                      </TableRow>
                    ) : (
                      policy.lines.map((line) => (
                        <TableRow key={line.id}>
                          <TableCell />
                          <TableCell className="text-neutral-500">{policy.vendor || '—'}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-2">
                              <span
                                className={`inline-block size-2 rounded-full ${LINE_COLORS[line.lineType]}`}
                              />
                              {line.lineLabel}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-neutral-500 tabular-nums">
                            {policy.boundDate}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-neutral-500 tabular-nums">
                            {policy.policyStart ?? (
                              <span className="text-amber-700 dark:text-amber-400">no start date</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {line.premium === null ? (
                              <span className="text-amber-700 dark:text-amber-400">no premium</span>
                            ) : (
                              formatCurrency(line.premium, WHOLE_DOLLARS)
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
